import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useCategories, useMasterData } from '../masterdata/queries';
import { useAssignableUsers } from '../users/queries';
import { useBulkUpdate } from './queries';
import { Select } from '../../components/Select';

interface BulkBarProps {
  selected: string[];
  onDone: () => void;
}

/**
 * Massenbearbeitung -- F1-20, K-9.
 *
 * **Nur die Felder, die man an vielen Vorgaengen zugleich aendert:**
 * Warteschlange, Prioritaet, Bearbeiter. Betreff oder Sachverhalt gehoeren
 * nicht dazu -- wer denselben Text in zwanzig Vorgaenge schriebe, meinte in
 * Wahrheit einen.
 *
 * **Sie erscheint erst mit einer Auswahl** und verschwindet danach wieder.
 * Eine dauerhaft sichtbare Leiste mit ausgegrauten Feldern beansprucht Platz
 * in der Ansicht, die am haeufigsten offen steht.
 *
 * **Was leer bleibt, bleibt unangetastet.** Der Server nimmt nur die Felder
 * entgegen, die gesetzt sind; ein leeres Auswahlfeld heisst "nicht aendern"
 * und nicht "leeren". Das ist der Unterschied, an dem eine
 * Massenbearbeitung sonst einen halben Bestand loescht.
 */
export function BulkBar({ selected, onDone }: BulkBarProps) {
  const { t } = useTranslation();
  const bulk = useBulkUpdate();

  const queues = useMasterData('queue');
  const priorities = useMasterData('priority');
  const users = useAssignableUsers();
  const categories = useCategories();

  if (selected.length === 0) return null;

  const apply = (field: string, value: string) => {
    if (value === '') return;
    bulk.mutate({ ticket_ids: selected, [field]: value }, { onSuccess: onDone });
  };

  return (
    <div className="notice notice--info" role="region" aria-label={t('tickets.bulkTitle')}>
      <p>{t('tickets.bulkSelected', { count: selected.length })}</p>

      <div className="row">
        <Select
          className="field__control"
          aria-label={t('tickets.queue')}
          value=""
          onChange={(event) => {
            apply('queue_id', event.target.value);
          }}
        >
          <option value="">{t('tickets.bulkQueue')}</option>
          {(queues.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </Select>

        <Select
          className="field__control"
          aria-label={t('tickets.priority')}
          value=""
          onChange={(event) => {
            apply('priority_id', event.target.value);
          }}
        >
          <option value="">{t('tickets.bulkPriority')}</option>
          {(priorities.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </Select>

        <Select
          className="field__control"
          aria-label={t('tickets.owner')}
          value=""
          onChange={(event) => {
            apply('owner_id', event.target.value);
          }}
        >
          <option value="">{t('tickets.bulkOwner')}</option>
          {(users.data ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name}
            </option>
          ))}
        </Select>

        <Select
          className="field__control"
          aria-label={t('tickets.category')}
          value=""
          onChange={(event) => {
            apply('category_id', event.target.value);
          }}
        >
          <option value="">{t('tickets.bulkCategory')}</option>
          {(categories.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </Select>

        <Button variant="ghost" onClick={onDone}>
          {t('tickets.bulkClear')}
        </Button>
      </div>

      {bulk.isError ? <ErrorNotice error={bulk.error} /> : null}
      {bulk.isSuccess ? (
        <p role="status">{t('tickets.bulkDone', { count: bulk.data.updated })}</p>
      ) : null}
    </div>
  );
}
